import { DeleteOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Popconfirm, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { deleteAllMeasurements } from '../../fetchHooks/deleteAllMeasurements';
import { deleteMeasurement } from '../../fetchHooks/deleteMeasurement';
import { useMyday } from '../../fetchHooks/useMyday.ts';
import type { MydayType } from '../../fetchHooks/useMyday.ts';
import type { MeasurementType } from '../../fetchHooks/useMyday.ts';

interface MeasurementsListProps {
  mydayId: number;
}

export const MeasurementsList = ({ mydayId }: MeasurementsListProps) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { data: myday, refetch: refetchMyday } = useMyday(mydayId?.toString() || '');

  if (!mydayId) return <></>;
  if (!myday) return <></>;
  const { measurements } = myday as MydayType;
  if (!measurements?.length) return <></>;

  const handleDeleteAll = () => {
    deleteAllMeasurements(mydayId, () => {
      refetchMyday();
      queryClient.invalidateQueries({ queryKey: ['useFilesOnly'] });
      queryClient.invalidateQueries({ queryKey: ['useMyday', mydayId] });
    });
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
        <Typography.Text>{t('myday.existingMeasurements') || 'Existing measurements'}:</Typography.Text>
        <Popconfirm
          title={t('myday.confirmDeleteAllMeasurements')}
          description={t('myday.confirmDeleteAllMeasurementsDescription', { count: measurements.length })}
          onConfirm={handleDeleteAll}
          onCancel={() => console.log('Canceled')}
          okText={t('common.yes')}
          cancelText={t('common.no')}
          okButtonProps={{ danger: true }}
        >
          <Button danger icon={<DeleteOutlined />} size="small" type="default">
            {t('myday.deleteAllMeasurements') || 'Delete All Measurements'}
          </Button>
        </Popconfirm>
      </div>

      {measurements.map((m: MeasurementType) => (
        <div key={m.id}>
          <Popconfirm
            title={t('myday.confirmDeleteMeasurement') || 'Are you sure to delete this measurement?'}
            onConfirm={() => {
              deleteMeasurement(m.id, refetchMyday);
              queryClient.invalidateQueries({ queryKey: ['useFilesOnly'] });
            }}
            onCancel={() => console.log('Canceled')}
            okText={t('common.yes')}
            cancelText={t('common.no')}
          >
            <Button icon={<DeleteOutlined />} className="delete-button" shape="round" size="small" type="text" />
          </Popconfirm>
          {m.healthMetricType?.description}: {m.value} {m.healthMetricType?.unit}
        </div>
      ))}
    </div>
  );
};
